import { useState } from "react";
import { api, formatAud } from "../../lib/api";
import StoreHeader from "../../components/layout/StoreHeader";
import StoreFooter from "../../components/layout/StoreFooter";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { RotateCcw, CheckCircle2, AlertTriangle, Image as ImgIcon, X, Search } from "lucide-react";
import { toast } from "sonner";

const RETURN_REASONS = [
  "Wrong part / didn't fit", "Faulty / not working",
  "Damaged in transit", "Different than described",
  "Changed my mind", "Other",
];

export default function Returns() {
  const [stage, setStage] = useState("lookup"); // lookup | select | done
  const [order, setOrder] = useState(null);
  const [lookup, setLookup] = useState({ order_number: "", email: "" });
  const [items, setItems] = useState([]); // {sku, qty, reason, selected}
  const [reason, setReason] = useState("");
  const [phone, setPhone] = useState("");
  const [images, setImages] = useState([]);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);

  const findOrder = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.get(
        `/returns/lookup/${encodeURIComponent(lookup.order_number)}?email=${encodeURIComponent(lookup.email)}`
      );
      setOrder(data);
      setItems(data.items.map((i) => ({ sku: i.sku, name: i.name, price: i.price, max: i.qty, qty: 0, reason: "", selected: false })));
      setStage("select");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Order not found");
    } finally { setBusy(false); }
  };

  const addImage = (file) => {
    if (!file) return;
    if (images.length >= 6) { toast.error("Up to 6 photos"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5MB)"); return; }
    const reader = new FileReader();
    reader.onload = (e) => setImages((arr) => [...arr, e.target.result]);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    const chosen = items.filter((i) => i.selected && i.qty > 0);
    if (chosen.length === 0) { toast.error("Select at least one item"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/returns", {
        order_number: lookup.order_number,
        customer_email: lookup.email,
        items: chosen.map((i) => ({ sku: i.sku, qty: i.qty, reason: i.reason })),
        reason, contact_phone: phone, images,
      });
      setDone(data);
      setStage("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white min-h-screen" data-testid="returns-page">
      <StoreHeader />

      <section className="max-w-3xl mx-auto px-4 md:px-8 py-10">
        <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#e31e24]">Customer support</div>
        <h1 className="font-heading text-4xl font-bold uppercase text-[#0b1a3b] mt-2">Request a return</h1>
        <p className="text-zinc-600 mt-2 max-w-prose">
          30-day return window on most parts. Submit a request below and we'll email a return label once approved.
        </p>

        {stage === "lookup" && (
          <form onSubmit={findOrder} className="mt-8 border border-zinc-200 rounded-sm p-6 md:p-8 space-y-4" data-testid="returns-lookup-form">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-[#1e5bb8]" />
              <div className="font-heading text-lg font-bold uppercase">Find your order</div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Order number *</Label>
                <Input required value={lookup.order_number} onChange={(e) => setLookup({ ...lookup, order_number: e.target.value })} placeholder="BCP-XXXXXX" className="mt-1 rounded-sm" data-testid="returns-order-number" />
              </div>
              <div>
                <Label>Email used at checkout *</Label>
                <Input required type="email" value={lookup.email} onChange={(e) => setLookup({ ...lookup, email: e.target.value })} className="mt-1 rounded-sm" data-testid="returns-email" />
              </div>
            </div>
            <Button type="submit" disabled={busy} className="w-full h-12 rounded-sm bcp-gradient-red font-heading font-bold uppercase tracking-wide" data-testid="returns-lookup-submit">
              {busy ? "Looking up…" : "Continue"}
            </Button>
          </form>
        )}

        {stage === "select" && order && (
          <form onSubmit={submit} className="mt-8 border border-zinc-200 rounded-sm p-6 md:p-8 space-y-4" data-testid="returns-select-form">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Order</div>
                <div className="font-heading text-2xl font-bold text-[#0b1a3b]">{order.order_number}</div>
                <div className="text-sm text-zinc-600">{order.customer_name} · {new Date(order.created_at).toLocaleDateString("en-AU")}</div>
              </div>
              <div className="text-right">
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Total</div>
                <div className="font-heading text-xl font-bold">{formatAud(order.total)}</div>
              </div>
            </div>

            <div>
              <Label>Items to return</Label>
              <div className="mt-2 border border-zinc-200 rounded-sm divide-y divide-zinc-200">
                {items.map((it, idx) => (
                  <div key={it.sku} className="p-3" data-testid={`returns-item-${it.sku}`}>
                    <div className="flex items-center gap-3">
                      <input type="checkbox" checked={it.selected}
                        onChange={(e) => setItems((arr) => arr.map((x, i) => i === idx ? { ...x, selected: e.target.checked, qty: e.target.checked ? 1 : 0 } : x))}
                        data-testid={`returns-item-toggle-${it.sku}`} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{it.name}</div>
                        <div className="text-xs font-mono text-zinc-500">{it.sku} · {formatAud(it.price)} · {it.max} on order</div>
                      </div>
                      <Input type="number" min="0" max={it.max} value={it.qty}
                        onChange={(e) => setItems((arr) => arr.map((x, i) => i === idx ? { ...x, qty: Number(e.target.value), selected: Number(e.target.value) > 0 } : x))}
                        className="w-20 rounded-sm" disabled={!it.selected} />
                    </div>
                    {it.selected && (
                      <select value={it.reason}
                        onChange={(e) => setItems((arr) => arr.map((x, i) => i === idx ? { ...x, reason: e.target.value } : x))}
                        className="mt-2 w-full px-3 py-2 border border-zinc-300 rounded-sm text-sm">
                        <option value="">Reason for this item…</option>
                        {RETURN_REASONS.map((r) => <option key={r}>{r}</option>)}
                      </select>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>Additional details (optional)</Label>
              <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full px-3 py-2 border border-zinc-300 rounded-sm" placeholder="Anything else we should know" data-testid="returns-detail-input" />
            </div>

            <div>
              <Label>Contact phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1 rounded-sm" />
            </div>

            <div>
              <Label className="flex items-center gap-1.5"><ImgIcon className="h-3.5 w-3.5" /> Photos (up to 6)</Label>
              <input type="file" accept="image/*" onChange={(e) => addImage(e.target.files[0])} className="mt-1 text-sm" data-testid="returns-image-input" />
              {images.length > 0 && (
                <div className="mt-2 grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {images.map((src, i) => (
                    <div key={i} className="relative aspect-square border border-zinc-200 rounded-sm overflow-hidden">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => setImages((arr) => arr.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white rounded-sm flex items-center justify-center"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-start gap-2 text-xs text-zinc-600 bg-amber-50 border-l-4 border-amber-400 p-3 rounded-sm">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
              <div>Items returned damaged may not be eligible for full credit. We'll notify you of any deductions before issuing the credit.</div>
            </div>

            <Button type="submit" disabled={busy} className="w-full h-12 rounded-sm bcp-gradient-red font-heading font-bold uppercase tracking-wide" data-testid="returns-submit">
              {busy ? "Submitting…" : "Submit return request"}
            </Button>
          </form>
        )}

        {stage === "done" && done && (
          <div className="mt-10 border border-green-500/40 bg-green-50 rounded-sm p-8 text-center" data-testid="returns-success">
            <CheckCircle2 className="h-10 w-10 mx-auto text-green-600" />
            <div className="font-heading text-2xl font-bold uppercase text-[#0b1a3b] mt-3">Return request submitted</div>
            <div className="text-zinc-600 mt-2">
              Reference <b className="font-mono">{done.rma_number}</b>. We'll email you within 1 business day with next steps.
            </div>
            <Button onClick={() => { setStage("lookup"); setDone(null); setOrder(null); setItems([]); setLookup({ order_number: "", email: "" }); setImages([]); setReason(""); setPhone(""); }} className="mt-6 rounded-sm" variant="outline" data-testid="returns-new-btn">
              <RotateCcw className="h-4 w-4 mr-2" /> Request another return
            </Button>
          </div>
        )}
      </section>

      <StoreFooter />
    </div>
  );
}
